import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import { useParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "@/hooks/useToast";

import {
    useImproveBatchLoader,
    parseSourceAnnotations,
    SourceDocument
} from "@/hooks/useImproveBatchLoader";
import { ImproveContext, ImproveContextType } from "./ImproveContext";
import { MediaItemFile, MediaItem } from "@/types/media";
import {
    useSetCurrentMedia,
    useSyncPromptLabels,
    useMarkLabelAsEdited,
    useSetImproveBatch,
    useSetIsLoadingImages,
    useSetHasImproveInitialized,
    useLimitedSources,
    useImproveAnnotationGroup,
    useHasImproveInitialized,
    useIsLoadingImages,
    useCurrentMedia,
    useRemoveSourceById,
    useImproveBatch,
    useCurrentWorkspace,
    useLoadMoreSources,
    useIncrementApprovedCount,
    useApprovedImageIds,
    useMarkImageAsApproved,
    useSetLimitedSources,
    useCurrentWorkflow,
    useCurrentWorkspaceApiKey
} from "@/store";
import { useMedia } from "@/contexts/MediaContext";
import { useModel } from "@/contexts/ModelContext";
import { Annotation } from "@/types/annotation";
import { generateColorFromText } from "@/components/annotation/colors";
import type { AnnotationsForFileAndFrameId } from "@/store";
import { annotationSyncQueue } from "@/services/annotationSyncQueue";
import {
    approveSource,
    uploadSourceForReview,
    ApproveSourceAnnotation
} from "@/utils/api/datasets";
import { firebaseStorage } from "@/utils/api/firebaseStorage";
import { streamInfer, InferenceServerPrediction } from "@/utils/api/inference";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";

/**
 * Download an image from a URL and convert it to a File object.
 * Uses the sourceId as the file.id to maintain consistency with the source document.
 * Sets skipUpload=true since these files are already hosted.
 * Stores the imageUrl so backend can use it directly instead of looking up in storage.
 */
async function downloadImageAsFile(
    url: string,
    filename: string,
    sourceId: string
): Promise<MediaItemFile> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" }) as MediaItemFile;
    // Use sourceId as the file ID to maintain consistency with source documents
    file.id = sourceId;
    // Mark as skipUpload since this file is already hosted
    file.skipUpload = true;
    // Store the image URL so backend can use it directly for inference
    file.imageUrl = url;
    return file;
}

/**
 * Convert source document predictions to Annotation format for editing in the app.
 * Annotations appear in sidebar cards and are editable by the user.
 *
 * @param source - The source document
 * @param fileId - The file ID to associate with annotations
 * @param annotationGroup - The annotation group key from dataset.annotation
 */
function convertSourceToAnnotations(
    source: SourceDocument,
    fileId: string,
    annotationGroup: string
): Annotation[] {
    const annotationData = parseSourceAnnotations(source, annotationGroup);
    if (!annotationData) {
        return [];
    }

    return annotationData.predictions.map((pred) => ({
        // Use detection_id as the annotation id for consistency
        id: pred.detection_id || uuidv4(),
        x: pred.x,
        y: pred.y,
        width: pred.width,
        height: pred.height,
        label: pred.class,
        color: generateColorFromText(pred.class),
        confidence: pred.confidence,
        draft: false,
        // Required fields for Annotation type
        fileId,
        frameId: fileId // For images, frameId equals fileId
    }));
}

/**
 * Update the URL's sourceId without triggering React Router navigation.
 * Uses history.replaceState for a smooth URL update without component remounting.
 */
function updateUrlSourceId(currentPath: string, newSourceId: string): void {
    const pathParts = currentPath.split("/").filter(Boolean);
    // pathParts should be [workspaceUrl, workflowUrl, step] or [workspaceUrl, workflowUrl, step, sourceId]
    if (pathParts.length >= 3) {
        const basePath = `/${pathParts.slice(0, 3).join("/")}`;
        const newPath = `${basePath}/${newSourceId}`;
        window.history.replaceState(null, "", newPath);
    }
}

export const ImproveContextProvider = ({ children }: { children: React.ReactNode }) => {
    const toast = useToast();

    // URL params - we read from useParams but update via history.replaceState to avoid re-renders
    const { sourceId: urlSourceId } = useParams<{ sourceId?: string }>();
    const location = useLocation();

    // Track the current sourceId in URL via ref to avoid stale closure issues
    const currentUrlSourceIdRef = useRef<string | undefined>(urlSourceId);

    // Keep ref in sync with URL param
    useEffect(() => {
        currentUrlSourceIdRef.current = urlSourceId;
    }, [urlSourceId]);

    // Data loading hook
    const { loading, error, improveBatch, loadImproveBatch, getSourceImageUrl } =
        useImproveBatchLoader();

    // Media and model contexts for adding items and annotations
    const { addMediaItems, orderedMediaItems, deleteMediaItem } = useMedia();
    const { setAnnotationsForFileAndFrameId, annotationsForFileAndFrameId } = useModel();

    // Get workspace and batch data for approve API
    const currentWorkspace = useCurrentWorkspace();
    const improveBatchData = useImproveBatch();

    // Zustand store actions
    const setCurrentMedia = useSetCurrentMedia();
    const syncPromptLabels = useSyncPromptLabels();
    const markLabelAsEdited = useMarkLabelAsEdited();
    const setImproveBatch = useSetImproveBatch();
    const setIsLoadingImages = useSetIsLoadingImages();
    const setHasInitialized = useSetHasImproveInitialized();

    // Zustand store state
    const limitedSources = useLimitedSources();
    const annotationGroup = useImproveAnnotationGroup();
    const hasInitialized = useHasImproveInitialized();
    const isLoadingImages = useIsLoadingImages();
    const currentMedia = useCurrentMedia();
    const removeSourceById = useRemoveSourceById();
    const loadMoreSources = useLoadMoreSources();
    const incrementApprovedCount = useIncrementApprovedCount();
    const approvedImageIds = useApprovedImageIds();
    const markImageAsApproved = useMarkImageAsApproved();
    const setLimitedSources = useSetLimitedSources();
    const workflow = useCurrentWorkflow();
    const apiKey = useCurrentWorkspaceApiKey();

    // Null image confirmation dialog state
    const [isNullConfirmOpen, setIsNullConfirmOpen] = useState(false);
    const nullConfirmResolveRef = useRef<((confirmed: boolean) => void) | null>(null);

    const confirmNullApproval = useCallback((): Promise<boolean> => {
        return new Promise((resolve) => {
            nullConfirmResolveRef.current = resolve;
            setIsNullConfirmOpen(true);
        });
    }, []);

    const handleNullConfirm = useCallback(() => {
        setIsNullConfirmOpen(false);
        nullConfirmResolveRef.current?.(true);
        nullConfirmResolveRef.current = null;
    }, []);

    const handleNullCancel = useCallback(() => {
        setIsNullConfirmOpen(false);
        nullConfirmResolveRef.current?.(false);
        nullConfirmResolveRef.current = null;
    }, []);

    // Sync improve batch data to Zustand store when loaded from hook
    // Pass urlSourceId as prioritySourceId to ensure it's included in limitedSources
    useEffect(() => {
        if (improveBatch) {
            console.log("[ImproveProvider] Syncing improveBatch to Zustand store:", {
                datasetId: improveBatch.datasetId,
                annotationGroup: improveBatch.annotationGroup,
                batchRef: improveBatch.batchRef,
                sourcesCount: improveBatch.sources.length,
                prioritySourceId: urlSourceId
            });
            setImproveBatch(
                {
                    sources: improveBatch.sources,
                    batchRef: improveBatch.batchRef,
                    datasetId: improveBatch.datasetId,
                    annotationGroup: improveBatch.annotationGroup
                },
                urlSourceId // Ensure this source is included in limitedSources
            );
        }
    }, [improveBatch, setImproveBatch, urlSourceId]);

    // Convert sources to MediaItems when sources are loaded
    useEffect(() => {
        // Skip if already initialized or currently loading
        if (hasInitialized || isLoadingImages) {
            return;
        }

        // Skip if no sources or annotation group yet
        if (limitedSources.length === 0 || !annotationGroup) {
            return;
        }

        // Mark as initializing immediately to prevent re-entry
        setHasInitialized(true);

        const loadImages = async () => {
            console.log(
                "[ImproveProvider] Starting to load images with annotationGroup:",
                annotationGroup
            );
            setIsLoadingImages(true);

            // Clear stale currentMedia from the previous page (e.g. deploy page)
            // so it doesn't interfere with initial source selection below.
            setCurrentMedia(undefined);

            try {
                // Build a map of sourceId -> source for annotation lookup
                const sourceMap = new Map(limitedSources.map((s) => [s.id, s]));

                // Download images in parallel, using sourceId as the file ID
                const downloadPromises = limitedSources.map(async (source) => {
                    const imageUrl = getSourceImageUrl(source);
                    const filename = source.name || `${source.id}.jpg`;
                    console.log("[ImproveProvider] Downloading image:", imageUrl);

                    try {
                        // Pass sourceId as the file ID
                        const file = await downloadImageAsFile(imageUrl, filename, source.id);
                        console.log(
                            "[ImproveProvider] Downloaded file:",
                            file.name,
                            "id:",
                            file.id,
                            "size:",
                            file.size
                        );
                        return file;
                    } catch (err) {
                        console.error(
                            `[ImproveProvider] Failed to download image ${source.id}:`,
                            err
                        );
                        return null;
                    }
                });

                const results = await Promise.all(downloadPromises);
                const validFiles = results.filter((file): file is MediaItemFile => file !== null);
                console.log("[ImproveProvider] Valid files downloaded:", validFiles.length);

                if (validFiles.length > 0) {
                    // Use the MediaProvider's addMediaItems to properly register them
                    console.log(
                        "[ImproveProvider] Calling addMediaItems with",
                        validFiles.length,
                        "files"
                    );
                    const addedItems = await addMediaItems(validFiles);
                    console.log(
                        "[ImproveProvider] addMediaItems returned:",
                        addedItems?.length,
                        "items"
                    );

                    // After files are added, load annotations from source documents
                    // Annotations are stored by fileId and frameId (for images, frameId = fileId)
                    // This makes them editable in the sidebar and visible in thumbnails
                    const allLabels = new Set<string>();

                    setAnnotationsForFileAndFrameId((prev) => {
                        const updated: AnnotationsForFileAndFrameId = { ...prev };

                        for (const file of validFiles) {
                            const source = sourceMap.get(file.id);
                            if (!source) {
                                console.log(
                                    `[ImproveProvider] No source found for file ${file.id}`
                                );
                                continue;
                            }

                            const annotations = convertSourceToAnnotations(
                                source,
                                file.id,
                                annotationGroup
                            );
                            console.log(
                                `[ImproveProvider] Converted ${annotations.length} annotations for file ${file.id}`,
                                annotations
                            );

                            if (annotations.length > 0) {
                                // Collect unique class labels
                                annotations.forEach((a) => allLabels.add(a.label));

                                // Store annotations under fileId -> frameId (for images, frameId = fileId)
                                if (!updated[file.id]) {
                                    updated[file.id] = {};
                                }
                                updated[file.id][file.id] = annotations;
                                console.log(
                                    `[ImproveProvider] Stored annotations at updated[${file.id}][${file.id}]`
                                );
                            }
                        }

                        console.log(
                            "[ImproveProvider] Final annotationsForFileAndFrameId:",
                            updated
                        );
                        return updated;
                    });

                    // Sync prompt labels so annotations become visible and thumbnails can be generated
                    // Also mark each label as edited so they show "Find Similar Objects" button instead of confidence slider
                    if (allLabels.size > 0) {
                        const labelsArray = Array.from(allLabels);
                        console.log("[ImproveProvider] Syncing prompt labels:", labelsArray);
                        syncPromptLabels(labelsArray);

                        // Mark all labels as edited (hasDiffs=true) since these are pre-existing annotations
                        labelsArray.forEach((label) => {
                            markLabelAsEdited(label);
                        });
                    }

                    // Set initial media item immediately (no setTimeout race condition).
                    // If sourceId is in URL, select that item; otherwise fall back to first.
                    if (addedItems && addedItems.length > 0) {
                        let initialItem: MediaItem | undefined;
                        let shouldRedirect = false;

                        // If sourceId is in URL, find that item
                        if (urlSourceId) {
                            initialItem = addedItems.find((item) => item.id === urlSourceId);
                            if (initialItem) {
                                console.log(
                                    "[ImproveProvider] Setting initial media from URL sourceId:",
                                    urlSourceId
                                );
                                currentUrlSourceIdRef.current = urlSourceId;
                            } else {
                                console.warn(
                                    "[ImproveProvider] sourceId from URL not found in loaded items:",
                                    urlSourceId
                                );
                            }
                        }

                        // Fall back to first item if no URL sourceId or not found
                        if (!initialItem) {
                            initialItem = addedItems[0];
                            shouldRedirect = true;
                            console.log(
                                "[ImproveProvider] Setting initial current media (first item):",
                                initialItem.id
                            );
                        }

                        setCurrentMedia({ file: initialItem, frame: initialItem, frameSegment: 0 });

                        // If no sourceId was in URL, update URL to include it (without navigation)
                        if (shouldRedirect) {
                            console.log(
                                "[ImproveProvider] Updating URL to include sourceId:",
                                initialItem.id
                            );
                            updateUrlSourceId(location.pathname, initialItem.id);
                            currentUrlSourceIdRef.current = initialItem.id;
                        }
                    }
                }

                console.log("[ImproveProvider] Initialization complete");
            } catch (err) {
                console.error("[ImproveProvider] Error loading improve batch images:", err);
                // Reset initialization flag on error so user can retry
                setHasInitialized(false);
            } finally {
                setIsLoadingImages(false);
            }
        };

        loadImages();
    }, [
        limitedSources,
        annotationGroup,
        hasInitialized,
        isLoadingImages,
        getSourceImageUrl,
        addMediaItems,
        setAnnotationsForFileAndFrameId,
        syncPromptLabels,
        markLabelAsEdited,
        setCurrentMedia,
        setIsLoadingImages,
        setHasInitialized,
        urlSourceId,
        location.pathname
    ]);

    // Update URL when current media changes (user navigates between images)
    useEffect(() => {
        const currentFileId = currentMedia?.file?.id;

        // Skip if no current media or still loading
        if (!currentFileId || isLoadingImages || !hasInitialized) {
            return;
        }

        // Skip if the URL already has this sourceId (use ref for current value)
        if (currentUrlSourceIdRef.current === currentFileId) {
            return;
        }

        // Only update if the file is in our limited sources (valid improve image)
        const isValidSource = limitedSources.some((s) => s.id === currentFileId);
        if (!isValidSource) {
            return;
        }

        console.log("[ImproveProvider] Updating URL with sourceId:", currentFileId);
        updateUrlSourceId(location.pathname, currentFileId);
        currentUrlSourceIdRef.current = currentFileId;
    }, [
        currentMedia?.file?.id,
        limitedSources,
        isLoadingImages,
        hasInitialized,
        location.pathname
    ]);

    // NOTE: We intentionally do NOT have a URL-to-media sync effect here.
    // The initial sourceId from URL is handled during initialization (loadImages).
    // After that, the user navigates via carousel which updates currentMedia,
    // and the media-to-URL effect updates the URL via replaceState.
    // Since replaceState doesn't create history entries, browser back/forward
    // will navigate away from the improve page entirely, not between images.

    // Track approval state
    const [isApproving, setIsApproving] = useState(false);

    /**
     * Handle approving the current image.
     *
     * This function performs optimistic UI updates:
     * 1. Finds the next image to navigate to
     * 2. Optimistically removes the image from the carousel (instant feedback)
     * 3. Navigates to the next image immediately
     * 4. In background: awaits pending syncs and performs approval
     *
     * The approval action runs in the background, allowing the user to continue
     * working immediately without waiting for the sync/approval to complete.
     */
    const handleApproveImage = useCallback(async () => {
        const sourceId = currentMedia?.file?.id;
        if (!sourceId) {
            console.warn("[ImproveProvider] Cannot approve: no current media");
            return;
        }

        // Validate we have all required data
        const workspaceId = currentWorkspace?.id;
        const datasetId = improveBatchData?.datasetId;
        const batchRef = improveBatchData?.batchRef;

        if (!workspaceId || !datasetId || !batchRef) {
            console.error("[ImproveProvider] Cannot approve: missing required data", {
                workspaceId,
                datasetId,
                batchRef
            });
            return;
        }

        // Extract batchId from batchRef (format: "datasetId/batchId")
        const batchId = batchRef.split("/")[1];
        if (!batchId) {
            console.error("[ImproveProvider] Cannot approve: invalid batchRef format", batchRef);
            return;
        }

        // Get current annotations for this image
        // For images, frameId equals fileId
        const currentAnnotations = annotationsForFileAndFrameId[sourceId]?.[sourceId] || [];

        // Get image dimensions from source document or media metadata
        const source = limitedSources.find((s) => s.id === sourceId);
        const mediaItem = currentMedia?.file;
        const imageWidth =
            source?.width ||
            mediaItem?.metadata?.naturalDimensions?.width ||
            mediaItem?.metadata?.renderedDimensions?.width ||
            0;
        const imageHeight =
            source?.height ||
            mediaItem?.metadata?.naturalDimensions?.height ||
            mediaItem?.metadata?.renderedDimensions?.height ||
            0;

        // Build annotation payload for API
        const annotation: ApproveSourceAnnotation = {
            image: { width: imageWidth, height: imageHeight },
            predictions: currentAnnotations.map((a) => ({
                x: a.x,
                y: a.y,
                width: a.width,
                height: a.height,
                class: a.label,
                confidence: a.confidence
            }))
        };

        // If no annotations, confirm with the user before approving as null
        if (annotation.predictions.length === 0) {
            const confirmed = await confirmNullApproval();
            if (!confirmed) return;
        }

        console.log("[ImproveProvider] Starting optimistic approval for source:", sourceId, {
            workspaceId,
            datasetId,
            batchId,
            annotationCount: annotation.predictions.length
        });

        // Check if this image was already approved (user is saving changes)
        const isAlreadyApproved = approvedImageIds.has(sourceId);

        // Find the current index and determine the next unapproved image
        const currentIndex = orderedMediaItems.findIndex((item) => item.id === sourceId);
        let nextItem: MediaItem | undefined;

        if (currentIndex !== -1 && orderedMediaItems.length > 1) {
            // Find the next unapproved image (prefer forward, then wrap around)
            const findNextUnapproved = (
                startIndex: number,
                direction: 1 | -1
            ): MediaItem | undefined => {
                let index = startIndex;
                for (let i = 0; i < orderedMediaItems.length - 1; i++) {
                    index =
                        (index + direction + orderedMediaItems.length) % orderedMediaItems.length;
                    const item = orderedMediaItems[index];
                    // Skip if this is the current item or if it's already approved (after marking current)
                    if (item.id !== sourceId && !approvedImageIds.has(item.id)) {
                        return item;
                    }
                }
                return undefined;
            };

            // Try forward first, then backward
            nextItem = findNextUnapproved(currentIndex, 1) || findNextUnapproved(currentIndex, -1);
        }

        // Step 1: Create annotation snapshot and mark the image as approved (shows checkmark overlay on thumbnail)
        // Serialize annotations for comparison later (to detect if user made changes)
        const annotationSnapshot = JSON.stringify(
            currentAnnotations.map((a) => ({
                x: a.x,
                y: a.y,
                width: a.width,
                height: a.height,
                label: a.label
            }))
        );
        console.log("[ImproveProvider] Marking image as approved:", sourceId);
        markImageAsApproved(sourceId, annotationSnapshot);

        // Only increment count if this is a new approval (not saving changes to already-approved image)
        if (!isAlreadyApproved) {
            incrementApprovedCount();
        }

        // Step 2: Navigate to next unapproved image immediately
        if (nextItem) {
            console.log("[ImproveProvider] Navigating to next unapproved image:", nextItem.id);
            setCurrentMedia({ file: nextItem, frame: nextItem, frameSegment: 0 });
            updateUrlSourceId(location.pathname, nextItem.id);
            currentUrlSourceIdRef.current = nextItem.id;
        } else {
            console.log("[ImproveProvider] No more unapproved images in batch");
            // All images approved - user can still navigate back to any image to make edits
        }

        // Step 4: Queue the actual approval in the background
        // This doesn't block the UI - user can continue working
        setIsApproving(true);

        // Run approval in background (don't await)
        (async () => {
            try {
                // Await any pending annotation syncs for this source
                console.log("[ImproveProvider] Awaiting pending syncs for source:", sourceId);
                await annotationSyncQueue.awaitSyncsForSource(sourceId);
                console.log("[ImproveProvider] Pending syncs completed for source:", sourceId);

                // Call approve source API
                const result = await approveSource({
                    workspaceId,
                    datasetId,
                    sourceId,
                    batchId,
                    annotation
                });

                console.log("[ImproveProvider] Approval completed for source:", sourceId, result);
            } catch (err) {
                console.error("[ImproveProvider] Approval failed:", err);
                toast.error("Failed to approve image. Please try again.");
                // The image is already removed optimistically, so we might need to
                // handle rollback or just show an error notification
            } finally {
                setIsApproving(false);
            }
        })();
    }, [
        currentMedia?.file?.id,
        currentMedia?.file,
        currentWorkspace?.id,
        improveBatchData?.datasetId,
        improveBatchData?.batchRef,
        annotationsForFileAndFrameId,
        limitedSources,
        orderedMediaItems,
        approvedImageIds,
        markImageAsApproved,
        incrementApprovedCount,
        setCurrentMedia,
        location.pathname,
        confirmNullApproval
    ]);

    // Status message while adding image (null = idle)
    const [addingImageStatus, setAddingImageStatus] = useState<string | null>(null);

    /**
     * Handle adding a new image from the + button in the improve carousel.
     *
     * Flow:
     * 1. Upload file to Firebase Storage → show image in carousel immediately
     * 2. Run workflow inference (overlay: "Running model on image...")
     * 3. Upload to review batch (overlay: "Saving...")
     * 4. Set annotations and clear overlay
     */
    const handleAddImageForReview = useCallback(
        async (files: File[]) => {
            const file = files[0];
            if (!file) return;

            const rapidDataId = workflow?.rapidDataId;
            if (!rapidDataId || !workflow?.id || !apiKey || !currentWorkspace) {
                console.error(
                    "[ImproveProvider] Cannot add image: missing workflow/apiKey/workspace"
                );
                return;
            }

            try {
                // Step 1: Upload to Firebase Storage and show image immediately
                setAddingImageStatus("Uploading...");
                const fileUuid = uuidv4();
                const storageUploadDir = currentWorkspace.url as string;
                const uploadResult = await firebaseStorage.uploadFile(
                    file,
                    fileUuid,
                    storageUploadDir
                );

                // Show the image in the carousel right away (before inference)
                // Use a temporary ID; we'll get the real sourceId after uploadSourceForReview
                const tempFile = await downloadImageAsFile(
                    uploadResult.downloadUrl,
                    file.name,
                    fileUuid // temporary ID
                );
                const addedItems = await addMediaItems([tempFile]);
                if (addedItems && addedItems.length > 0) {
                    const newItem =
                        addedItems.find((item) => item.id === fileUuid) || addedItems[0];
                    setCurrentMedia({ file: newItem, frame: newItem, frameSegment: 0 });
                }

                // Step 2: Run workflow inference
                setAddingImageStatus("Running model on image...");
                const rawPredictions = await new Promise<InferenceServerPrediction[]>(
                    (resolve, reject) => {
                        const allPredictions: InferenceServerPrediction[] = [];

                        streamInfer(
                            {
                                fileId: fileUuid,
                                inferenceType: InferenceTypes.WORKFLOW,
                                workflowId: workflow.id!,
                                apiKey: apiKey,
                                fps: 1,
                                imageUrl: uploadResult.downloadUrl
                            },
                            {
                                onResult: ({ result }: { result: any }) => {
                                    // Workflow results wrap predictions:
                                    // result.outputs[].predictions.predictions
                                    const outputs = result?.outputs || [];
                                    const predictionsOutput = outputs.find(
                                        (o: any) => o.predictions
                                    );
                                    const preds = predictionsOutput?.predictions?.predictions || [];
                                    allPredictions.push(...preds);
                                },
                                onComplete: () => resolve(allPredictions),
                                onError: ({ message }: { message: string }) =>
                                    reject(new Error(message))
                            }
                        );
                    }
                );

                // Step 3: Get image dimensions
                const imageDimensions = await new Promise<{ width: number; height: number }>(
                    (resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            resolve({ width: img.naturalWidth, height: img.naturalHeight });
                            URL.revokeObjectURL(img.src);
                        };
                        img.onerror = () => {
                            resolve({ width: 640, height: 480 });
                            URL.revokeObjectURL(img.src);
                        };
                        img.src = URL.createObjectURL(file);
                    }
                );

                // Step 4: Upload to review batch
                setAddingImageStatus("Saving...");
                const annotation: ApproveSourceAnnotation = {
                    image: imageDimensions,
                    predictions: rawPredictions.map((pred) => ({
                        x: pred.x,
                        y: pred.y,
                        width: pred.width,
                        height: pred.height,
                        class: pred.class,
                        confidence: pred.confidence
                    }))
                };

                const { sourceId } = await uploadSourceForReview({
                    rapidDataId,
                    imageUrl: uploadResult.downloadUrl,
                    annotation
                });

                // Step 5: Add source to limitedSources
                const newSource: SourceDocument = {
                    id: sourceId,
                    owner: currentWorkspace.id as string,
                    name: file.name,
                    width: imageDimensions.width,
                    height: imageDimensions.height
                };
                setLimitedSources([...limitedSources, newSource]);

                // Step 6: Replace temp media item with the real sourceId
                // Remove the temp item and add the real one
                deleteMediaItem(fileUuid);
                const hostedFile = await downloadImageAsFile(
                    uploadResult.downloadUrl,
                    file.name,
                    sourceId
                );
                const finalItems = await addMediaItems([hostedFile]);

                // Step 7: Set annotations for the new image
                if (rawPredictions.length > 0 && annotationGroup) {
                    const annotations: Annotation[] = rawPredictions.map((pred) => ({
                        id: pred.detection_id || uuidv4(),
                        x: pred.x,
                        y: pred.y,
                        width: pred.width,
                        height: pred.height,
                        label: pred.class,
                        color: generateColorFromText(pred.class),
                        confidence: pred.confidence,
                        draft: false,
                        fileId: sourceId,
                        frameId: sourceId
                    }));

                    setAnnotationsForFileAndFrameId((prev: AnnotationsForFileAndFrameId) => ({
                        ...prev,
                        [sourceId]: { [sourceId]: annotations }
                    }));

                    const labels = [...new Set(rawPredictions.map((p) => p.class))];
                    syncPromptLabels(labels);
                    labels.forEach((label) => markLabelAsEdited(label));
                }

                // Step 8: Navigate to the real source
                if (finalItems && finalItems.length > 0) {
                    const newItem =
                        finalItems.find((item) => item.id === sourceId) || finalItems[0];
                    setCurrentMedia({ file: newItem, frame: newItem, frameSegment: 0 });
                    updateUrlSourceId(location.pathname, sourceId);
                    currentUrlSourceIdRef.current = sourceId;
                }
            } catch (err) {
                console.error("[ImproveProvider] Error adding image for review:", err);
                toast.error("Failed to add image. Please try again.");
            } finally {
                setAddingImageStatus(null);
            }
        },
        [
            workflow,
            apiKey,
            currentWorkspace,
            limitedSources,
            annotationGroup,
            addMediaItems,
            deleteMediaItem,
            setAnnotationsForFileAndFrameId,
            syncPromptLabels,
            markLabelAsEdited,
            setCurrentMedia,
            setLimitedSources,
            location.pathname,
            toast
        ]
    );

    // Auto-load more sources when running low on unapproved images
    // This effect watches the approvedImageIds and loads more when we have fewer unapproved images than threshold
    const LOW_IMAGES_THRESHOLD = 5;
    const [isLoadingMoreSources, setIsLoadingMoreSources] = useState(false);

    useEffect(() => {
        // Skip if not initialized, already loading, or no batch data
        if (!hasInitialized || isLoadingImages || isLoadingMoreSources || !improveBatch) {
            return;
        }

        // Check if we need to load more - count unapproved images
        const unapprovedCount = orderedMediaItems.filter(
            (item) => !approvedImageIds.has(item.id)
        ).length;
        if (unapprovedCount > LOW_IMAGES_THRESHOLD) {
            return;
        }

        // Load more sources
        const newSourceIds = loadMoreSources();
        if (newSourceIds.length === 0) {
            console.log("[ImproveProvider] No more sources to load");
            return;
        }

        console.log("[ImproveProvider] Auto-loading", newSourceIds.length, "more sources");
        setIsLoadingMoreSources(true);

        // Load the new sources as media items
        const loadNewSources = async () => {
            try {
                // Get the new source documents from limitedSources (they were just added by loadMoreSources)
                // We need to get the updated limitedSources from the store
                const allSources = improveBatch.sources;
                const newSources = allSources.filter((s) => newSourceIds.includes(s.id));

                // Download images in parallel
                const downloadPromises = newSources.map(async (source) => {
                    const imageUrl = getSourceImageUrl(source);
                    const filename = source.name || `${source.id}.jpg`;
                    try {
                        const response = await fetch(imageUrl);
                        if (!response.ok) throw new Error(`Failed to download: ${response.status}`);
                        const blob = await response.blob();
                        const file = new File([blob], filename, {
                            type: blob.type || "image/jpeg"
                        }) as MediaItemFile;
                        file.id = source.id;
                        file.skipUpload = true;
                        file.imageUrl = imageUrl;
                        return file;
                    } catch (err) {
                        console.error(`[ImproveProvider] Failed to download ${source.id}:`, err);
                        return null;
                    }
                });

                const results = await Promise.all(downloadPromises);
                const validFiles = results.filter((f): f is MediaItemFile => f !== null);

                if (validFiles.length > 0) {
                    // Add the new files to the media provider
                    const addedItems = await addMediaItems(validFiles);

                    // Load annotations for the new files
                    const allLabels = new Set<string>();
                    const sourceMap = new Map(newSources.map((s) => [s.id, s]));

                    setAnnotationsForFileAndFrameId((prev) => {
                        const updated = { ...prev };
                        for (const file of validFiles) {
                            const source = sourceMap.get(file.id);
                            if (!source || !annotationGroup) continue;

                            const annotations = convertSourceToAnnotations(
                                source,
                                file.id,
                                annotationGroup
                            );
                            if (annotations.length > 0) {
                                annotations.forEach((a) => allLabels.add(a.label));
                                if (!updated[file.id]) updated[file.id] = {};
                                updated[file.id][file.id] = annotations;
                            }
                        }
                        return updated;
                    });

                    // Sync any new labels
                    if (allLabels.size > 0) {
                        const labelsArray = Array.from(allLabels);
                        syncPromptLabels(labelsArray);
                        labelsArray.forEach((label) => markLabelAsEdited(label));
                    }

                    console.log("[ImproveProvider] Loaded", addedItems?.length, "more images");
                }
            } catch (err) {
                console.error("[ImproveProvider] Error loading more sources:", err);
            } finally {
                setIsLoadingMoreSources(false);
            }
        };

        loadNewSources();
    }, [
        hasInitialized,
        isLoadingImages,
        isLoadingMoreSources,
        improveBatch,
        orderedMediaItems,
        approvedImageIds,
        loadMoreSources,
        getSourceImageUrl,
        addMediaItems,
        setAnnotationsForFileAndFrameId,
        annotationGroup,
        syncPromptLabels,
        markLabelAsEdited
    ]);

    // Context only provides orchestration methods
    // State is accessed via Zustand selectors directly in components
    const contextValue: ImproveContextType = useMemo(
        () => ({
            loadImproveBatch,
            getSourceImageUrl,
            handleApproveImage,
            isApproving,
            handleAddImageForReview,
            addingImageStatus
        }),
        [
            loadImproveBatch,
            getSourceImageUrl,
            handleApproveImage,
            isApproving,
            handleAddImageForReview,
            addingImageStatus
        ]
    );

    return (
        <ImproveContext.Provider value={contextValue}>
            {children}
            <ModalWithIcon
                isOpen={isNullConfirmOpen}
                onClose={handleNullCancel}
                onSubmit={handleNullConfirm}
                onCancel={handleNullCancel}
                type="info"
                size="md"
                icon="far fa-image"
                title="Approve Null Image?"
                submitButtonText="Approve as Null"
                cancelButtonText="Cancel"
            >
                <p className="text-center text-sm text-secondary">
                    A null image is one that contains none of the objects your model is meant to
                    detect. These images help your model learn what <strong>not</strong> to flag.
                </p>
            </ModalWithIcon>
        </ImproveContext.Provider>
    );
};
